/**
 * CompleteTaskDialog - "Complete task" (T-09).
 *
 * Every task completion opens this small box first (it replaced the old
 * "Mark complete?" browser confirm). It holds an optional note - "Hong said
 * decision next week" - that is stored with the completion and shows on the
 * task-completed entry of the timeline. Leaving it empty completes the task
 * with no note at all.
 *
 * On the GLOBAL KSA tab there is no timeline, so the box asks only to confirm
 * (withNote is false there).
 *
 * Driven by useCompleteTask: onComplete(note) resolves to an error message
 * (shown here, the box stays open) or null. If this was the last open task on
 * a lead or an open opportunity, the hook then asks "What's the next step?"
 * (T-04): the note comes first, then the prompt.
 *
 * Also exports TaskNotice: a plain message box for the one failure the hook
 * cannot show in a dialog that is still open (the next step was saved but the
 * task itself could not be completed).
 *
 * Rendered into document.body, like NextStepPrompt: the Dashboard's side
 * column is a size container, which would otherwise trap a fixed overlay.
 */
import { useState } from 'react';
import { useDraggableDialog } from '../../hooks/useDraggableDialog';
import { createPortal } from 'react-dom';
import { taggedOf, taggedTitle } from './tagged';

const GREEN = '#1B5E20';
const FONT = "'Montserrat', sans-serif";
export const NOTE_MAX = 2000; // the server keeps at most 2000 characters

const overlay = {
  position: 'fixed',
  inset: 0,
  zIndex: 1300,
  background: 'rgba(0,0,0,0.35)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
  fontFamily: FONT,
};
const card = {
  width: '100%',
  maxWidth: 460,
  maxHeight: '92vh',
  overflowY: 'auto',
  background: '#fff',
  borderRadius: 12,
  boxShadow: '0 18px 50px rgba(0,0,0,0.25)',
  boxSizing: 'border-box',
};
const label = {
  fontSize: 10,
  fontWeight: 700,
  color: '#5A5A5A',
  letterSpacing: 0.6,
  textTransform: 'uppercase',
  marginBottom: 4,
  display: 'block',
};
const stop = (e) => e.stopPropagation();
const inBody = (node) => (typeof document !== 'undefined' ? createPortal(node, document.body) : node);

export default function CompleteTaskDialog({ task, withNote = true, onComplete, onCancel }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const drag = useDraggableDialog(); // moved by its header (10/9/26)

  const submit = async () => {
    if (busy) return;
    setBusy(true);
    setError('');
    let err = null;
    try {
      err = await onComplete(withNote ? note.trim() : '');
    } catch {
      err = 'Could not complete the task - try again.';
    }
    // on success the hook closes this box; a failure keeps it open with the reason
    setBusy(false);
    if (err) setError(err);
  };

  return inBody(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="complete-task-title"
      onClick={stop}
      onMouseDown={stop}
      onContextMenu={stop}
      onKeyDown={(e) => {
        // stop here so the editor underneath does not also close on Escape
        e.stopPropagation();
        if (e.key === 'Escape' && !busy) onCancel();
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit();
      }}
      style={overlay}
    >
      <div style={{ ...card, ...drag.style }} className="go-pop-in" ref={drag.ref} onPointerDown={drag.onPointerDown}>
        <div data-dialog-handle="" style={{ padding: '18px 22px 12px', borderBottom: '1px solid #EEF1EA' }}>
          <div id="complete-task-title" style={{ fontSize: 17, fontWeight: 800, color: '#0D0D0D' }}>
            Complete task
          </div>
          <div style={{ fontSize: 11.5, color: '#555', marginTop: 6, lineHeight: 1.5, wordBreak: 'break-word' }}>
            <b>&ldquo;{task.description}&rdquo;</b>
            {task.owner ? <> · {task.owner}</> : null}
            {taggedOf(task).length ? <span title={taggedTitle(taggedOf(task))}> · {taggedTitle(taggedOf(task))}</span> : null}
          </div>
        </div>

        <div style={{ padding: '14px 22px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {withNote && (
            <div>
              <label style={label} htmlFor="complete-task-note">
                Note (optional) - goes on the timeline
              </label>
              <textarea
                id="complete-task-note"
                autoFocus
                value={note}
                maxLength={NOTE_MAX}
                onChange={(e) => setNote(e.target.value)}
                placeholder="For example: Hong said decision next week"
                style={{
                  padding: '8px 10px',
                  borderRadius: 6,
                  border: '1px solid #D8DED4',
                  fontSize: 12,
                  fontFamily: FONT,
                  outline: 'none',
                  background: '#fff',
                  color: '#0D0D0D',
                  width: '100%',
                  boxSizing: 'border-box',
                  minHeight: 70,
                  resize: 'vertical',
                  lineHeight: 1.45,
                }}
              />
            </div>
          )}

          {error && (
            <div
              role="alert"
              style={{
                background: '#FFEBEE',
                border: '1px solid #FFCDD2',
                color: '#B71C1C',
                borderRadius: 6,
                padding: '7px 10px',
                fontSize: 11.5,
                fontWeight: 600,
              }}
            >
              {error}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 2 }}>
            <button
              type="button"
              onClick={onCancel}
              disabled={busy}
              title="Nothing changes - the task stays open"
              style={{
                padding: '8px 14px',
                borderRadius: 6,
                border: '1px solid #DDD',
                background: '#fff',
                color: '#555',
                fontSize: 11.5,
                fontWeight: 600,
                cursor: busy ? 'default' : 'pointer',
                fontFamily: FONT,
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={busy}
              autoFocus={!withNote}
              style={{
                padding: '8px 16px',
                borderRadius: 6,
                border: 'none',
                fontSize: 11.5,
                fontWeight: 700,
                fontFamily: FONT,
                background: busy ? '#C8D6C9' : GREEN,
                color: '#fff',
                cursor: busy ? 'default' : 'pointer',
              }}
            >
              {busy ? 'Completing…' : 'Complete'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** A message the user must acknowledge (a completion that only half worked). */
export function TaskNotice({ message, onClose }) {
  const drag = useDraggableDialog();
  return inBody(
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="task-notice-title"
      onClick={stop}
      onMouseDown={stop}
      onContextMenu={stop}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === 'Escape' || e.key === 'Enter') onClose();
      }}
      style={overlay}
    >
      <div
        style={{ ...card, maxWidth: 420, padding: '18px 22px', ...drag.style }}
        className="go-pop-in"
        ref={drag.ref}
        onPointerDown={drag.onPointerDown}
      >
        <div data-dialog-handle="" id="task-notice-title" style={{ fontSize: 14, fontWeight: 800, color: '#B71C1C' }}>
          Task not completed
        </div>
        <div style={{ fontSize: 12, color: '#333', marginTop: 8, lineHeight: 1.5, wordBreak: 'break-word' }}>{message}</div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
          <button
            type="button"
            onClick={onClose}
            autoFocus
            style={{
              padding: '8px 16px',
              borderRadius: 6,
              border: 'none',
              background: GREEN,
              color: '#fff',
              fontSize: 11.5,
              fontWeight: 700,
              cursor: 'pointer',
              fontFamily: FONT,
            }}
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
}
